import { message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  refetchMappedEventRelated,
  refetchMetricRelated,
  refetchDimensionRelated,
  refetchFlowRelated,
} from '@src/emp-3.0/utils/refetch';

import {
  postActivateMetric,
  postCreateMetric,
  putActivateMetric,
  putUpdateMetric,
  requestDeleteMetric,
  requestDeleteVersion,
  requestMetric,
  requestMetricList,
} from './index';
import { DeleteVersionType } from '@src/emp-3.0/components/Management/components/BulkAction/typed';

export const METRIC_LIST_QUERY_KEY = 'METRIC_LIST_QUERY_KEY';

export const useMetricList = () => {
  return useQuery([METRIC_LIST_QUERY_KEY], async () => {
    const res = await requestMetricList();
    return res?.data;
  });
};

export const useGetMetricById = (props?: any) => {
  return useQuery(
    ['getMetricById', props?.id],
    async () => {
      const res = await requestMetric(props?.id);
      return res.data;
    },
    {
      enabled: !!props?.id,
      cacheTime: 0,
      onError: (error: any) => {
        message.error(error?.response?.data?.msg);
      },
      ...props?.options,
    },
  );
};

export const useActivateMetric = () => {
  return useMutation(async (payload) => {
    const res = await postActivateMetric(payload);
    refetchMetricRelated();
    refetchMappedEventRelated();
    return res?.data;
  });
};

export const usePutActivateMetric = () => {
  return useMutation(async ({ metric_id, payload }: { metric_id: number; payload: any }) => {
    const res = await putActivateMetric(metric_id, payload);
    refetchMetricRelated();
    refetchMappedEventRelated();
    return res?.data;
  });
};

export const useCreateMetric = () => {
  return useMutation(async (payload: any) => {
    const { newMetric } = payload;
    const res = await postCreateMetric(newMetric);
    refetchMetricRelated();
    refetchMappedEventRelated();
    return res?.data;
  });
};

export const useUpdateMetric = () => {
  return useMutation(async (payload: any) => {
    const { id, newMetric } = payload;
    const res = await putUpdateMetric(id, newMetric);
    refetchMetricRelated();
    refetchMappedEventRelated();
    return res?.data;
  });
};

export const useDeleteMetric = () => {
  return useMutation(async (payload: any) => {
    const { id } = payload;
    const res = await requestDeleteMetric(id);
    refetchMetricRelated();
    refetchMappedEventRelated();
    return res?.data;
  });
};

export const useDeleteVersion = () => {
  return useMutation(async (payload: { ids: number[]; type: DeleteVersionType }) => {
    const { ids, type } = payload;
    const res = await requestDeleteVersion(ids, type);
    switch (type) {
      case DeleteVersionType.mapped_events:
        refetchMappedEventRelated();
        break;
      case DeleteVersionType.metrics:
        refetchMetricRelated();
        refetchMappedEventRelated();
        break;
      case DeleteVersionType.custom_dimension:
        refetchDimensionRelated();
        break;
      case DeleteVersionType.flow:
        refetchFlowRelated();
        break;
      default:
    }
    return res?.data;
  });
};
